import axios from 'axios';
import { apiUrl } from './apiConfig';
import { isNativeApp } from './platform';

const TOKEN_KEY = 'token';
const USER_KEY = 'user';
const REFRESH_WHEN_LEFT_MS = 7 * 24 * 60 * 60 * 1000;

let refreshInFlight = null;

function readItem(key) {
  try {
    const sessionVal = sessionStorage.getItem(key);
    if (sessionVal && sessionVal !== 'undefined') return sessionVal;
  } catch {
    /* ignore */
  }
  try {
    const localVal = localStorage.getItem(key);
    if (localVal && localVal !== 'undefined') return localVal;
  } catch {
    /* ignore */
  }
  return null;
}

function writePair(key, value, remember) {
  const primary = remember ? localStorage : sessionStorage;
  const other = remember ? sessionStorage : localStorage;
  try {
    primary.setItem(key, value);
  } catch {
    /* ignore */
  }
  try {
    other.removeItem(key);
  } catch {
    /* ignore */
  }
}

function persistOnThisDevice(remember) {
  return remember !== false || isNativeApp();
}

function tokenPayload(token) {
  return JSON.parse(atob(token.split('.')[1]));
}

export function isRemembered() {
  try {
    if (sessionStorage.getItem(TOKEN_KEY)) return false;
  } catch {
    /* ignore */
  }
  try {
    return Boolean(localStorage.getItem(TOKEN_KEY));
  } catch {
    return true;
  }
}

export const setToken = (token, remember = isRemembered()) => {
  if (!token) {
    clearAuthData();
    return;
  }
  writePair(TOKEN_KEY, token, persistOnThisDevice(remember));
};

export const setUser = (user, remember = isRemembered()) => {
  if (user == null) {
    try {
      sessionStorage.removeItem(USER_KEY);
    } catch {
      /* ignore */
    }
    try {
      localStorage.removeItem(USER_KEY);
    } catch {
      /* ignore */
    }
    return;
  }
  const raw = typeof user === 'string' ? user : JSON.stringify(user);
  writePair(USER_KEY, raw, persistOnThisDevice(remember));
};

export const getToken = () => readItem(TOKEN_KEY);

export const getUser = () => {
  const raw = readItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const clearAuthData = () => {
  [localStorage, sessionStorage].forEach((store) => {
    try {
      store.removeItem(TOKEN_KEY);
      store.removeItem(USER_KEY);
    } catch {
      /* ignore */
    }
  });
};

export const isTokenValid = () => {
  const token = getToken();
  if (!token) return false;

  try {
    const payload = tokenPayload(token);
    return payload.exp * 1000 > Date.now();
  } catch (e) {
    return false;
  }
};

export async function refreshAccessToken() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const token = getToken();
    if (!token) return null;
    try {
      const payload = tokenPayload(token);
      if (payload.exp * 1000 <= Date.now()) return null;
    } catch {
      return null;
    }
    const { data } = await axios.post(
      apiUrl('/alogin/refresh'),
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000,
      }
    );
    const next = data?.access_token;
    if (next) {
      setToken(next);
      return next;
    }
    return null;
  })()
    .catch(() => null)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

export const refreshTokenIfNeeded = async () => {
  const token = getToken();
  if (!token) return false;

  try {
    const payload = tokenPayload(token);
    const left = payload.exp * 1000 - Date.now();
    if (left <= 0) return false;
    if (left < REFRESH_WHEN_LEFT_MS) {
      return Boolean(await refreshAccessToken());
    }
    return true;
  } catch (e) {
    return false;
  }
};
